import React from 'react';
import { PedidoListaItem } from '../types/pedidosNuevos';
import EstadoBadge from './EstadoBadge';
import BarraProgreso from './BarraProgreso';
import { formatearFechaMedia } from '../utils/dateUtils';
import { useAuth } from '../hooks/useAuth';
import {
  Calendar,
  User,
  ChefHat,
  Package,
  Mail,
  FileText,
  Eye,
  DollarSign,
} from 'lucide-react';

// Formato monetario chileno: $146.440 (sin decimales, punto separador).
const formatearMonedaCLP = (valor: number): string =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  }).format(valor);

interface TarjetaPedidoProps {
  pedido: PedidoListaItem;
  onVerDetalle?: (pedido: PedidoListaItem) => void;
  onAccion?: (pedido: PedidoListaItem, accion: string) => void;
  mostrarCliente?: boolean;
  accionesDisponibles?: string[];
}

/**
 * Tarjeta para mostrar información resumida de un pedido
 * Diseño compacto de 2 líneas
 */
const TarjetaPedido: React.FC<TarjetaPedidoProps> = ({
  pedido,
  onVerDetalle,
  onAccion,
  mostrarCliente = true,
  accionesDisponibles = [],
}) => {
  // Helper compartido en utils/dateUtils — evita el desfase de TZ
  // que sufría new Date(fechaISO).toLocaleDateString cuando la
  // fecha es un DATE de BD serializado como medianoche UTC.
  const formatearFecha = formatearFechaMedia;

  // El costo del pedido (TD-025) es información administrativa: solo
  // admin lo ve. Cliente/chef/cocina no necesitan ver el costo interno
  // de producción.
  const { user } = useAuth();
  const esAdmin = user?.rol === 'administrador';

  // Fix tanda2 T2-16 (2026-06-30): resaltar visualmente los pedidos
  // cuya fecha_entrega ya pasó (queda en cocina sin entregar). Antes
  // sólo se distinguían por el badge del estado, que es sutil.
  // Comparamos strings ISO 'YYYY-MM-DD' — evita drift por TZ.
  const hoyIso = new Date().toISOString().slice(0, 10);
  const fechaEntregaIso = (pedido.fecha_entrega || '').slice(0, 10);
  const esVencido =
    !!fechaEntregaIso &&
    fechaEntregaIso < hoyIso &&
    pedido.estado !== 'despachado' &&
    pedido.estado !== 'cancelado';

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-lg border hover:shadow-md transition-shadow ${
      esVencido
        ? 'border-red-400 dark:border-red-600 border-2 shadow-red-100 dark:shadow-red-900/20'
        : 'border-gray-200 dark:border-gray-700'
    }`}>
      {/* LÍNEA 1: Información Principal */}
      <div className="flex items-center gap-4 p-4">
        {/* ID Pedido + Estado */}
        <div className="flex items-center gap-2 min-w-[140px]">
          <h3 className="text-sm font-bold text-gray-900 dark:text-white">
            {pedido.codigo || `#${pedido.id}`}
          </h3>
          <EstadoBadge estado={pedido.estado} size="sm" />
        </div>

        {/* Cliente + Faena (operación) */}
        {mostrarCliente && (
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <User className="w-4 h-4 text-gray-400 flex-shrink-0" />
            <div className="flex flex-col min-w-0">
              <span className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {pedido.cliente_codigo && (
                  <span className="text-blue-600 dark:text-blue-400 mr-1">
                    {pedido.cliente_codigo}
                  </span>
                )}
                {pedido.cliente_razon_social || pedido.cliente_nombre}
              </span>
              {pedido.operacion_nombre && (
                <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
                  Faena: {pedido.operacion_nombre}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Minuta */}
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <ChefHat className="w-4 h-4 text-gray-400 flex-shrink-0" />
          <span className="text-sm text-gray-600 dark:text-gray-300 truncate">
            {pedido.minuta_nombre}
          </span>
        </div>

        {/* Fecha de Entrega — Fix tanda2 T2-16 */}
        <div className="flex items-center gap-2 min-w-[130px]">
          <Calendar
            className={`w-4 h-4 flex-shrink-0 ${
              esVencido ? 'text-red-500 dark:text-red-400' : 'text-gray-400'
            }`}
          />
          <span
            className={`text-sm ${
              esVencido
                ? 'text-red-700 dark:text-red-300 font-semibold'
                : 'text-gray-600 dark:text-gray-300'
            }`}
          >
            {formatearFecha(pedido.fecha_entrega)}
          </span>
          {esVencido && (
            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-100 dark:bg-red-900/40 text-red-800 dark:text-red-200 uppercase tracking-wide">
              Vencido
            </span>
          )}
        </div>

        {/* Botón Ver Detalle */}
        {onVerDetalle && (
          <button
            onClick={() => onVerDetalle(pedido)}
            className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
            title="Ver detalle"
          >
            <Eye className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* LÍNEA SEPARADORA */}
      <div className="border-t border-gray-200 dark:border-gray-700"></div>

      {/* LÍNEA 2: Estadísticas y Acciones */}
      <div className="flex items-center justify-between px-4 py-3">
        {/* Estadísticas */}
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <Package className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {pedido.total_platos} platos
            </span>
          </div>
          <div className="flex items-center gap-2">
            <FileText className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {pedido.total_items} recetas
            </span>
          </div>

          {/* Costo del pedido — solo admin (TD-025). Calculado en runtime
              desde ingredientes_receta × costo_unitario actual (no se
              congela al despachar — ver TD-027). */}
          {esAdmin && (
            <div
              className="flex items-center gap-2"
              title="Costo interno de producción (ingredientes × cantidades). No es precio de venta."
            >
              <DollarSign className="w-4 h-4 text-gray-400" />
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                {formatearMonedaCLP(pedido.costo_pedido ?? 0)}
              </span>
            </div>
          )}

          {/* Indicador de transcripción */}
          {!!pedido.es_transcripcion && (
            <div className="flex items-center gap-1.5 text-xs text-yellow-600 dark:text-yellow-400">
              <Mail className="w-3 h-3" />
              <span>Transcrito</span>
            </div>
          )}
        </div>

        {/* Botones de Acción */}
        {accionesDisponibles.length > 0 && onAccion && (
          <div className="flex gap-2">
            {accionesDisponibles.map((accion) => {
              const esAprobar = accion === 'aprobar';
              const esCancelar = accion === 'cancelar';
              const esRechazar = accion === 'rechazar';
              const esMarcarListo =
                accion === 'marcar-listo' || accion === 'marcar_listo';

              return (
                <button
                  key={accion}
                  onClick={() => onAccion(pedido, accion)}
                  className={`
                    px-4 py-1.5 text-sm font-medium rounded-lg transition-colors
                    ${
                      esAprobar
                        ? 'bg-green-600 text-white hover:bg-green-700'
                        : esCancelar || esRechazar
                          ? 'bg-red-600 text-white hover:bg-red-700'
                          : 'bg-blue-600 text-white hover:bg-blue-700'
                    }
                  `}
                >
                  {esAprobar
                    ? 'Aprobar'
                    : esCancelar
                      ? 'Cancelar'
                      : esRechazar
                        ? 'Rechazar'
                        : esMarcarListo
                          ? 'Marcar Listo'
                          : accion}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Barra de progreso (solo para pedidos en preparación) */}
      {pedido.estado === 'en_preparacion' && pedido.progreso && (
        <>
          <div className="border-t border-gray-200 dark:border-gray-700"></div>
          <div className="px-4 py-3">
            <BarraProgreso
              porcentaje={pedido.progreso.porcentaje}
              porcionesCompletadas={pedido.progreso.porciones_completadas}
              porcionesTotales={pedido.progreso.porciones_totales}
              size="md"
            />
          </div>
        </>
      )}

      {/* Observaciones (opcional, se expande si existen) */}
      {pedido.observaciones && (
        <>
          <div className="border-t border-gray-200 dark:border-gray-700"></div>
          <div className="px-4 py-2 bg-gray-50 dark:bg-gray-900/50">
            <p className="text-xs text-gray-600 dark:text-gray-400 italic">
              <span className="font-semibold">Obs:</span> {pedido.observaciones}
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default TarjetaPedido;
